import React from 'react';
import styled from 'styled-components';
import Chip from './Chip';

/**
 * ChipGroup Component - Container for multiple selectable chips
 *
 * @param {Object} props
 * @param {Array<{value: string, label: string, icon?: string}>} props.options - Array of chip options
 * @param {string|string[]} props.selectedValues - Selected value(s) - string for single-select, array for multi-select
 * @param {boolean} [props.multiSelect=false] - Allow multiple selections
 * @param {Function} props.onChange - Callback when selection changes (receives new value(s))
 * @param {string} [props.className] - Additional CSS class
 * @param {boolean} [props.disabled] - Whether the chip group is disabled
 * @param {string} [props.ariaLabel] - ARIA label for the group
 * @param {string} [props.ariaLabelledBy] - ID of the label element
 */
const ChipGroup = ({
  options = [],
  selectedValues,
  multiSelect = false,
  onChange,
  className,
  disabled = false,
  ariaLabel,
  ariaLabelledBy,
}) => {
  const handleChipSelect = (value) => {
    if (disabled) return;

    if (multiSelect) {
      const currentSelection = Array.isArray(selectedValues) ? selectedValues : [];

      // Toggle selection
      const isSelected = currentSelection.includes(value);

      if (isSelected) {
        // Don't allow deselecting if it's the last selected item
        if (currentSelection.length === 1) {
          return; // Prevent deselection of last chip
        }
        // Remove from selection
        const newSelection = currentSelection.filter(v => v !== value);
        onChange(newSelection);
      } else {
        // Add to selection
        const newSelection = [...currentSelection, value];
        onChange(newSelection);
      }
    } else {
      // Single select - replace selection
      onChange(value);
    }
  };

  const isSelected = (value) => {
    if (multiSelect) {
      return Array.isArray(selectedValues) && selectedValues.includes(value);
    } else {
      return selectedValues === value;
    }
  };

  // Calculate selection count for ARIA label
  const selectedCount = multiSelect && Array.isArray(selectedValues)
    ? selectedValues.length
    : (selectedValues ? 1 : 0);

  const ariaLabelText = multiSelect
    ? `${selectedCount} of ${options.length} selected`
    : undefined;

  return (
    <ChipGroupContainer
      role="group"
      aria-label={ariaLabel || ariaLabelText}
      aria-labelledby={ariaLabelledBy}
      className={className}
    >
      {options.map(option => (
        <Chip
          key={option.value}
          label={option.label}
          selected={isSelected(option.value)}
          onSelect={() => handleChipSelect(option.value)}
          disabled={disabled}
          icon={option.icon}
        />
      ))}
    </ChipGroupContainer>
  );
};

const ChipGroupContainer = styled.div`
  /* Layout */
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 36px;

  /* Ensure chips don't get cut off */
  align-items: flex-start;

  /* Responsive adjustments */
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

export default ChipGroup;
